import { useCallback, useEffect, useRef } from "react";
import { useGoodsAdjustColumnDefs } from "./columnDefs";
import {
  GridReadyEvent,
  RowDoubleClickedEvent,
  IServerSideGetRowsParams,
} from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import AgGrid from "components/UI/AgGrid";
import { AgGridReact } from "ag-grid-react";
import { dateFilterModel } from "utils/AgGridFilter";
import { GOODS_ADJUST_VIEW } from "services/AgGrid/GoodsAdjustAggrid";
import CustomizedBoxTable from "components/Custom/CustomizedBoxTable";

type Props = {
  status: string | null;
  height?: string | number;
};

const GoodsAdjustTable = ({ status, height }: Props) => {
  const gridRef = useRef<AgGridReact<any>>(null);
  const navigate = useNavigate();

  const graphQLClientWms: GraphQLClient =
    createGraphQLClientWithMiddleware("wms");

  const columnDefs = useGoodsAdjustColumnDefs();
  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;
      const { created_date, scanned_date, ...otherFilter } = filterModel;
      const formatFilter = {
        ...otherFilter,
        created_date: dateFilterModel(created_date),
      };
      const formatSort = [
        ...sortModel,
        { colId: "created_date", sort: "desc" },
      ];
      try {
        const { GoodAdjustsViewAggrid } = await graphQLClientWms.request(
          GOODS_ADJUST_VIEW,
          {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel: formatSort,
            },
          }
        );
        params.success({
          rowData: GoodAdjustsViewAggrid.results as any[],
          rowCount: GoodAdjustsViewAggrid.count as number,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onFilterChanged = useCallback(
    (params: any) => {
      const statusInstance = params.api.getFilterInstance("aggrid_status");
      switch (status) {
        case "draft":
        case "finished":
        case "cancelled":
          statusInstance.setModel({ values: [status] });
          break;
        default:
          statusInstance.setModel({
            values: ["draft", "finished"],
          });
          break;
      }
      params.api.onFilterChanged();
    },
    [status]
  );

  const onGridReady = (params: GridReadyEvent) => {
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  }, [gridRef, onFilterChanged]);

  const rowSelectHandler = (params: RowDoubleClickedEvent) => {
    const { id } = params.data;
    navigate(`/inventory/goods-adjust/${id}`);
  };

  return (
    <CustomizedBoxTable height={height}>
      <AgGrid
        ref={gridRef}
        columnDefs={columnDefs}
        // height={670}
        onRowDoubleClicked={rowSelectHandler}
        onGridReady={onGridReady}
        path={`/inventory/goods-adjust`}
      />
    </CustomizedBoxTable>
  );
};

export default GoodsAdjustTable;
